import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Search,
  ChevronRight,
  Network,
  CheckCircle2,
  PauseCircle,
  Building2,
  X,
  Loader2,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { DiagnosticTabs } from "../components/diagnostic/primitives";
import {
  getDiagnosticChains,
  getDiagnosticChainStats,
  createDiagnosticChain,
  editDiagnosticChain,
  formatDateTime,
} from "../services/diagnosticLabsService";

const PAGE_SIZE = 20;

const getCurrentRole = () => {
  const authRole = localStorage.getItem("authRole");
  const roleMapping = {
    wallet_superadmin: "SuperAdmin",
    wallet_finance: "Finance",
    wallet_ops: "Ops",
    wallet_support: "Support",
  };
  return roleMapping[authRole] || null;
};

const isActive = (chain) => chain?.active !== false;

/* ----------------------------- status badge ----------------------------- */
/* ------------------------------- stat card ------------------------------ */
const StatCard = ({ icon: Icon, label, value, accent, loading, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: index * 0.06, ease: "easeOut" }}
    className="relative overflow-hidden bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
        <div className="mt-2 h-9 flex items-end">
          {loading ? (
            <div className="h-7 w-12 rounded-md bg-gray-100 animate-pulse" />
          ) : (
            <motion.span
              key={value}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl font-bold text-gray-900 leading-none"
            >
              {value}
            </motion.span>
          )}
        </div>
      </div>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent.bg} ${accent.fg}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className={`absolute bottom-0 left-0 right-0 h-1 ${accent.bar}`} />
  </motion.div>
);

/* --------------------------- quick-create modal -------------------------- */
const CreateChainModal = ({ open, onClose, onCreated, role }) => {
  const [form, setForm] = useState({ brand_name: "", legal_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ brand_name: "", legal_name: "" });
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.brand_name.trim()) return setError("Brand name is required.");

    setSubmitting(true);
    setError("");
    try {
      const resp = await createDiagnosticChain(role, {
        brand_name: form.brand_name.trim(),
        legal_name: form.legal_name.trim(),
      });
      const chain = resp?.msg || resp;
      onCreated(chain);
    } catch (err) {
      setError(err.message || "Failed to create chain.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50";

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Network className="w-4.5 h-4.5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">New Diagnostic Chain</h3>
                <p className="text-xs text-gray-400">Brand name — refine the rest later</p>
              </div>
            </div>
            <button
              onClick={() => !submitting && onClose()}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand name <span className="text-red-500">*</span>
              </label>
              <input
                autoFocus
                className={inputCls}
                placeholder="e.g. Apex Diagnostics"
                value={form.brand_name}
                onChange={(e) => setForm((f) => ({ ...f, brand_name: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Legal entity name
              </label>
              <input
                className={inputCls}
                placeholder="optional — corporate parent if any"
                value={form.legal_name}
                onChange={(e) => setForm((f) => ({ ...f, legal_name: e.target.value }))}
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => !submitting && onClose()}
                disabled={submitting}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Creating..." : "Create chain"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

/* ------------------------------ main page ------------------------------- */
const DiagnosticLabsManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const currentRole = getCurrentRole();
  const canAccess = currentRole === "Ops" || currentRole === "SuperAdmin";

  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const [page, setPage] = useState(() => Number(searchParams.get("page") || 1));
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const [cityInput, setCityInput] = useState(() => searchParams.get("city") || "");
  const [searchFilter, setSearchFilter] = useState(() => searchParams.get("search") || "");
  const [cityFilter, setCityFilter] = useState(() => searchParams.get("city") || "");
  const [pagination, setPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });

  const debounceRef = useRef(null);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      setStats(await getDiagnosticChainStats(currentRole));
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", String(page));
    if (searchFilter.trim()) params.set("search", searchFilter.trim());
    if (cityFilter.trim()) params.set("city", cityFilter.trim());
    setSearchParams(params, { replace: true });
  }, [page, searchFilter, cityFilter, setSearchParams]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchFilter(searchInput.trim());
      setCityFilter(cityInput.trim());
      setPage(1);
    }, 600);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput, cityInput]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await getDiagnosticChains(currentRole, {
          page,
          items: PAGE_SIZE,
          search: searchFilter,
          city: cityFilter,
        });
        const items = resp?.msg?.items || resp?.items || [];
        const pg = resp?.msg?.pagination || resp?.pagination;
        setChains(Array.isArray(items) ? items : []);
        if (pg) {
          setPagination({ ...pg, total_pages: Math.ceil((pg.total || 0) / (pg.items || PAGE_SIZE)) });
        }
      } catch (err) {
        setError(err.message || "Failed to load diagnostic chains.");
        setChains([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess, currentRole, page, searchFilter, cityFilter]);

  const handleToggleStatus = async (chain, e) => {
    e.stopPropagation();
    const next = !isActive(chain);
    // optimistic
    setChains((prev) =>
      prev.map((c) => (c.id === chain.id ? { ...c, active: next } : c))
    );
    try {
      await editDiagnosticChain(currentRole, chain.id, { active: next });
      loadStats();
    } catch {
      setChains((prev) =>
        prev.map((c) => (c.id === chain.id ? { ...c, active: !next } : c))
      );
    }
  };

  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access Diagnostic Labs.</p>
        </div>
      </div>
    );
  }

  const STAT_CARDS = [
    { key: "total", label: "Total Chains", icon: Network, accent: { bg: "bg-blue-50", fg: "text-blue-600", bar: "bg-blue-500" } },
    { key: "active", label: "Active", icon: CheckCircle2, accent: { bg: "bg-emerald-50", fg: "text-emerald-600", bar: "bg-emerald-500" } },
    { key: "inactive", label: "Inactive", icon: PauseCircle, accent: { bg: "bg-amber-50", fg: "text-amber-600", bar: "bg-amber-500" } },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/subscriber-wallet")}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all"
              aria-label="Back to home"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">Diagnostic Labs</h1>
              <p className="text-sm text-gray-400">Onboard &amp; manage labs and chains</p>
            </div>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="self-start sm:self-auto px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm hover:shadow flex items-center gap-2 transition-all"
          >
            <Plus className="w-4 h-4" />
            Create Chain
          </button>
        </div>

        {/* Module tabs */}
        <DiagnosticTabs active="chains" />

        {/* Stats overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CARDS.map((c, i) => (
            <StatCard
              key={c.key}
              index={i}
              icon={c.icon}
              label={c.label}
              accent={c.accent}
              loading={statsLoading}
              value={stats?.[c.key] ?? 0}
            />
          ))}
        </div>

        {/* Chains panel */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Diagnostic Chains</h2>
            </div>

            <div className="flex flex-col lg:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by brand, legal entity or GSTIN"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <div className="relative lg:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by city"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                />
              </div>
            </div>

          </div>

          {error && (
            <div className="px-6 py-3 text-sm text-red-700 bg-red-50 border-b border-red-100">{error}</div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Legal Entity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {chains.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-16 text-center">
                      {loading ? (
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <span className="text-sm">Loading chains...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3 text-gray-400">
                          <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                            <Network className="w-6 h-6 text-gray-300" />
                          </div>
                          <p className="text-sm font-medium text-gray-500">No diagnostic chains yet</p>
                          <button
                            onClick={() => setCreateOpen(true)}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            + Create your first chain
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  chains.map((chain) => {
                    const active = isActive(chain);
                    return (
                      <tr
                        key={chain.id}
                        onClick={() => navigate(`/subscriber-wallet/labs/chains/${chain.id}`)}
                        className={`group hover:bg-blue-50/40 cursor-pointer transition-colors ${active ? "" : "bg-gray-50/50"}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center font-semibold text-sm shrink-0">
                              {(chain.brand_name || "?").charAt(0).toUpperCase()}
                            </div>
                            <span className={`text-sm font-medium ${active ? "text-gray-900" : "text-gray-400"}`}>
                              {chain.brand_name || "N/A"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{chain.legal_name || "—"}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{chain.hq_city || "—"}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                            <Building2 className="w-3.5 h-3.5 text-gray-400" />
                            {chain.member_labs_count ?? 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(chain.created_at)}</td>
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={active}
                            onClick={(e) => handleToggleStatus(chain, e)}
                            className="relative inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full"
                          >
                            <span className={`block w-10 h-5 rounded-full transition-colors ${active ? "bg-emerald-500" : "bg-gray-300"}`} />
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${active ? "translate-x-5" : "translate-x-0"}`} />
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors ml-auto" />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.items + 1}–
                {Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${pagination.page === 1
                    ? "text-gray-300 border-gray-200 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-50 border-gray-300"
                    }`}
                >
                  Prev
                </button>
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${pagination.page >= (pagination.total_pages || 1)
                    ? "text-gray-300 border-gray-200 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-50 border-gray-300"
                    }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateChainModal
        open={createOpen}
        role={currentRole}
        onClose={() => setCreateOpen(false)}
        onCreated={(chain) => {
          setCreateOpen(false);
          if (chain?.id) navigate(`/subscriber-wallet/labs/chains/${chain.id}`);
        }}
      />
    </div>
  );
};

export default DiagnosticLabsManagement;
